import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Check, Search, X } from 'lucide-react';

type FieldBase = {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
};

function FieldFrame({
  id,
  label,
  hint,
  error,
  required,
  children,
}: FieldBase & { id: string; required?: boolean | undefined; children: ReactNode }) {
  const description = [hint ? `${id}-hint` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={`ui-field${error ? ' ui-field--error' : ''}`}>
      <label htmlFor={id}>
        {label} {required ? <span aria-hidden="true">*</span> : null}
      </label>
      {children}
      {hint ? (
        <span className="ui-field__hint" id={`${id}-hint`}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="ui-field__error" id={`${id}-error`} role="alert">
          {error}
        </span>
      ) : null}
      {description ? <span className="sr-only">Campo con información adicional</span> : null}
    </div>
  );
}

export type TextFieldProps = FieldBase & InputHTMLAttributes<HTMLInputElement>;
export function TextField({
  label,
  hint,
  error,
  id: providedId,
  required,
  ...props
}: TextFieldProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} required={required}>
      <input
        {...props}
        id={id}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy || undefined}
      />
    </FieldFrame>
  );
}

export type SelectOption = { value: string; label: string };
export type SelectFieldProps = FieldBase & {
  id?: string;
  required?: boolean;
  disabled?: boolean;
  value: string;
  placeholder?: string;
  options: readonly SelectOption[];
  onValueChange: (value: string) => void;
};
export function SelectField({
  label,
  hint,
  error,
  id: providedId,
  required,
  disabled = false,
  value,
  placeholder = 'Selecciona una opción',
  options,
  onValueChange,
}: SelectFieldProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const listboxId = `${id}-listbox`;
  const [open, setOpen] = useState(false);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const [activeIndex, setActiveIndex] = useState(Math.max(selectedIndex, 0));
  const selected = options[selectedIndex];
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  function choose(index: number) {
    const option = options[index];
    if (!option) return;
    onValueChange(option.value);
    setActiveIndex(index);
    setOpen(false);
  }
  function handleKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open) choose(activeIndex);
      else setOpen(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    setOpen(true);
    setActiveIndex((current) => {
      if (event.key === 'Home') return 0;
      if (event.key === 'End') return options.length - 1;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      return (current + direction + options.length) % options.length;
    });
  }
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} required={required}>
      <div
        className={`ui-select${open ? ' ui-select--open' : ''}`}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
        }}
      >
        <button
          id={id}
          className="ui-select__trigger"
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy || undefined}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={handleKeyDown}
        >
          <span className={selected ? undefined : 'ui-select__placeholder'}>
            {selected?.label ?? placeholder}
          </span>
          <svg aria-hidden="true" viewBox="0 0 20 20">
            <path d="m5 7.5 5 5 5-5" />
          </svg>
        </button>
        {open ? (
          <div className="ui-select__menu" id={listboxId} role="listbox" aria-label={label}>
            {options.map((option, index) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={`ui-select__option${index === activeIndex ? ' ui-select__option--active' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(index)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span>{option.label}</span>
                {option.value === value ? <span aria-hidden="true">✓</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </FieldFrame>
  );
}

export type SearchSelectOption = { value: string; label: string; meta?: string };
export type SearchSelectFieldProps = FieldBase & {
  id?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  value: string;
  placeholder?: string;
  emptyMessage?: string;
  options: readonly SearchSelectOption[];
  onQueryChange?: (query: string) => void;
  onValueChange: (value: string) => void;
};

export function SearchSelectField({
  label,
  hint,
  error,
  id: providedId,
  required,
  disabled = false,
  loading = false,
  value,
  placeholder = 'Buscar y seleccionar',
  emptyMessage = 'No se encontraron resultados.',
  options,
  onQueryChange,
  onValueChange,
}: SearchSelectFieldProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const listboxId = `${id}-listbox`;
  const selected = options.find((option) => option.value === value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  const normalize = (text: string) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const visibleOptions = onQueryChange
    ? options
    : options.filter((option) =>
        normalize(`${option.label} ${option.meta ?? ''}`).includes(normalize(query)),
      );
  const choose = (option: SearchSelectOption) => {
    onValueChange(option.value);
    setQuery('');
    setOpen(false);
  };
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} required={required}>
      <div
        className={`ui-search-select${open ? ' ui-search-select--open' : ''}`}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
        }}
      >
        <div className="ui-search-select__control">
          <Search aria-hidden="true" className="ui-search-select__search-icon" />
          <input
            id={id}
            role="combobox"
            autoComplete="off"
            disabled={disabled}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy || undefined}
            placeholder={placeholder}
            value={open ? query : (selected?.label ?? query)}
            onFocus={() => {
              setQuery('');
              setOpen(true);
              onQueryChange?.('');
            }}
            onChange={(event) => {
              const nextQuery = event.currentTarget.value;
              setQuery(nextQuery);
              setOpen(true);
              if (value) onValueChange('');
              onQueryChange?.(nextQuery);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setOpen(false);
            }}
          />
          {value ? (
            <button
              type="button"
              className="ui-search-select__clear"
              aria-label={`Limpiar selección de ${label}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onValueChange('');
                setQuery('');
                onQueryChange?.('');
              }}
            >
              <X aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {selected && !open ? (
          <div className="ui-search-select__selection" aria-live="polite">
            <Check aria-hidden="true" />
            <span>{selected.meta ?? 'Selección confirmada'}</span>
          </div>
        ) : null}
        {open ? (
          <div className="ui-search-select__menu" id={listboxId} role="listbox" aria-label={label}>
            {loading ? <p className="ui-search-select__empty">Buscando…</p> : null}
            {!loading && visibleOptions.length === 0 ? (
              <p className="ui-search-select__empty">{emptyMessage}</p>
            ) : null}
            {!loading
              ? visibleOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    className="ui-search-select__option"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    <span>
                      <strong>{option.label}</strong>
                      {option.meta ? <small>{option.meta}</small> : null}
                    </span>
                    {option.value === value ? <Check aria-hidden="true" /> : null}
                  </button>
                ))
              : null}
          </div>
        ) : null}
      </div>
    </FieldFrame>
  );
}

export type TextAreaFieldProps = FieldBase & TextareaHTMLAttributes<HTMLTextAreaElement>;
export function TextAreaField({
  label,
  hint,
  error,
  id: providedId,
  required,
  ...props
}: TextAreaFieldProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} required={required}>
      <textarea
        {...props}
        id={id}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy || undefined}
      />
    </FieldFrame>
  );
}

export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="ui-form-section">
      <legend>{title}</legend>
      <p>{description}</p>
      <div className="ui-form-grid">{children}</div>
    </fieldset>
  );
}

export function ConfirmationDialog({
  open,
  busy,
  title,
  description,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  title: string;
  description: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        event.preventDefault();
        onCancel();
      }
      if (event.key !== 'Tab') return;
      const first = cancelRef.current;
      const last = confirmRef.current;
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel, open]);

  if (!open) return null;
  return (
    <div className="ui-dialog-backdrop">
      <section
        className="ui-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <span className="ui-dialog__eyebrow">Confirmación requerida</span>
        <h2 id={titleId}>{title}</h2>
        <p id={descriptionId}>{description}</p>
        <div className="ui-dialog__actions">
          <button
            ref={cancelRef}
            type="button"
            className="ui-button ui-button--secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Volver y revisar
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="ui-button ui-button--primary"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Enviando…' : 'Confirmar envío'}
          </button>
        </div>
      </section>
    </div>
  );
}

export type CanonicalFormValues = {
  reference: string;
  contact: string;
  category: string;
  notes: string;
};

type Errors = Partial<Record<keyof CanonicalFormValues, string>>;
export type FormEvidenceMode = 'default' | 'errors' | 'dialog' | 'success';

const evidenceValues: CanonicalFormValues = {
  reference: 'UI-014',
  contact: 'revision@example.test',
  category: 'accessibility',
  notes: 'Valores técnicos para comprobar conservación y confirmación.',
};

export function FormGallery({
  onSubmit,
  evidenceMode = 'default',
}: {
  onSubmit?: (values: CanonicalFormValues) => Promise<void>;
  evidenceMode?: FormEvidenceMode;
}) {
  const [values, setValues] = useState<CanonicalFormValues>(
    evidenceMode === 'dialog' || evidenceMode === 'success'
      ? evidenceValues
      : { reference: '', contact: '', category: '', notes: '' },
  );
  const [errors, setErrors] = useState<Errors>(
    evidenceMode === 'errors'
      ? {
          reference: 'Escribe una referencia técnica.',
          contact: 'Escribe un correo válido.',
          category: 'Selecciona una categoría.',
        }
      : {},
  );
  const [dialogOpen, setDialogOpen] = useState(evidenceMode === 'dialog');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(evidenceMode === 'success');
  const submitRef = useRef<HTMLButtonElement>(null);
  const submittingRef = useRef(false);

  function validate(): Errors {
    const next: Errors = {};
    if (!values.reference.trim()) next.reference = 'Escribe una referencia técnica.';
    if (!/^\S+@\S+\.\S+$/.test(values.contact)) next.contact = 'Escribe un correo válido.';
    if (!values.category) next.category = 'Selecciona una categoría.';
    return next;
  }

  function requestConfirmation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submittingRef.current) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    setSuccess(false);
    const firstError = (['reference', 'contact', 'category'] as const).find(
      (name) => nextErrors[name],
    );
    if (firstError) {
      requestAnimationFrame(() => document.getElementById(`canonical-${firstError}`)?.focus());
      return;
    }
    setDialogOpen(true);
  }

  function closeDialog() {
    if (busy) return;
    setDialogOpen(false);
    requestAnimationFrame(() => submitRef.current?.focus());
  }

  async function confirm() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      await (onSubmit?.(values) ?? new Promise<void>((resolve) => window.setTimeout(resolve, 650)));
      setDialogOpen(false);
      setSuccess(true);
      requestAnimationFrame(() => submitRef.current?.focus());
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  }

  function update(name: keyof CanonicalFormValues, value: string) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    setSuccess(false);
  }

  return (
    <section className="ui-form-gallery" aria-labelledby="canonical-form-title">
      <div className="ui-form-gallery__intro">
        <div>
          <span className="ui-form-gallery__step">01 · Ejemplo técnico</span>
          <h2 id="canonical-form-title">Patrón de captura y confirmación</h2>
          <p>Los campos conservan sus valores al validar, revisar y cancelar la confirmación.</p>
        </div>
        <span className="ui-required-note">
          <span aria-hidden="true">*</span> Obligatorio
        </span>
      </div>
      {success ? (
        <div className="ui-form-success" role="status">
          Envío técnico confirmado una sola vez. Los datos permanecen visibles.
        </div>
      ) : null}
      <form noValidate onSubmit={requestConfirmation}>
        <FormSection
          title="Datos de la muestra"
          description="Contenido demostrativo sin información de negocio."
        >
          <TextField
            id="canonical-reference"
            label="Referencia técnica"
            required
            value={values.reference}
            onChange={(event) => update('reference', event.target.value)}
            hint="Ejemplo: UI-014"
            error={errors.reference}
            autoComplete="off"
          />
          <TextField
            id="canonical-contact"
            label="Correo de revisión"
            required
            type="email"
            value={values.contact}
            onChange={(event) => update('contact', event.target.value)}
            hint="Solo se valida el formato; no se envía."
            error={errors.contact}
            autoComplete="email"
          />
          <SelectField
            id="canonical-category"
            label="Categoría de prueba"
            required
            value={values.category}
            onValueChange={(value) => update('category', value)}
            error={errors.category}
            options={[
              { value: 'accessibility', label: 'Accesibilidad' },
              { value: 'interaction', label: 'Interacción' },
              { value: 'visual', label: 'Evidencia visual' },
            ]}
          />
          <TextField
            id="canonical-disabled"
            label="Identificador generado"
            value="AUTO-M01.4"
            disabled
            hint="Ejemplo de control deshabilitado."
            readOnly
          />
          <TextAreaField
            id="canonical-notes"
            label="Notas técnicas"
            value={values.notes}
            onChange={(event) => update('notes', event.target.value)}
            hint={`${values.notes.length}/240 caracteres`}
            maxLength={240}
            rows={4}
          />
        </FormSection>
        <div className="ui-form-actions">
          <p>Al continuar podrás revisar la acción antes de confirmarla.</p>
          <button
            ref={submitRef}
            className="ui-button ui-button--primary"
            type="submit"
            disabled={busy}
          >
            Revisar envío
          </button>
        </div>
      </form>
      <ConfirmationDialog
        open={dialogOpen}
        busy={busy}
        title="¿Confirmar esta muestra técnica?"
        description="La confirmación simula un único envío y mantiene todos los valores escritos. No se conecta con una API."
        onCancel={closeDialog}
        onConfirm={() => void confirm()}
      />
    </section>
  );
}
